import type { ExportListResponse, ExportSummary } from '@/lib/api/reports-client.js';
import type { Pagination } from '@/lib/api/types.js';
import { cn } from '@/lib/utils.js';
import { ExportsRow } from './exports-row.js';

const STATUS_LABELS: Record<ExportSummary['status'], string> = {
  queued: 'En cola',
  running: 'En curso',
  completed: 'Completado',
  failed: 'Fallido',
  cancelled: 'Cancelado',
};

interface ExportsTableProps {
  data: ExportListResponse;
  cancellingIds: Set<string>;
  onDownload: (exportId: string) => void;
  onCancel: (exportId: string) => void;
  onPageChange: (page: number) => void;
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

export function ExportsTableSkeleton() {
  return (
    <div data-testid="exports-skeleton" className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <TableHead />
        </thead>
        <tbody>
          {(['r0', 'r1', 'r2', 'r3', 'r4'] as const).map((rowKey) => (
            <tr
              key={rowKey}
              className="border-b border-[var(--color-qup-border-dark)] even:bg-[var(--color-qup-bg-dark)]/40"
            >
              {(['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6'] as const).map((colKey) => (
                <td key={colKey} className="px-4 py-3">
                  <div className="h-3.5 animate-pulse rounded bg-[var(--color-qup-border-dark)]" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function TableHead() {
  return (
    <tr className="border-b border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]">
      {['Archivo', 'Estado', 'Filas / Tamaño', 'Solicitado', 'Completado', 'Error', ''].map(
        (col) => (
          <th
            key={col}
            className="sticky top-0 bg-[var(--color-qup-bg-card-dark)] px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-[var(--color-qup-text-muted-dark)]"
          >
            {col}
          </th>
        ),
      )}
    </tr>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function TablePagination({
  pagination,
  onPageChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
}) {
  const { page_number, total_pages, total_items } = pagination;
  if (total_pages <= 1) return null;

  const hasPrev = page_number > 1;
  const hasNext = page_number < total_pages;

  const btnCls = (enabled: boolean) =>
    cn(
      'flex h-7 min-w-[2rem] items-center justify-center rounded border px-2 text-xs transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
      enabled
        ? 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)] hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)]'
        : 'cursor-not-allowed border-[var(--color-qup-border-dark)]/40 text-[var(--color-qup-text-muted-dark)]/40',
    );

  return (
    <div className="flex items-center justify-between px-4 py-3 text-xs text-[var(--color-qup-text-muted-dark)]">
      <span>
        {total_items.toLocaleString('es')} exportaciones · Página {page_number} de {total_pages}
      </span>
      <nav aria-label="Paginación de exportaciones" className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page_number - 1)}
          disabled={!hasPrev}
          aria-label="Página anterior"
          className={btnCls(hasPrev)}
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page_number + 1)}
          disabled={!hasNext}
          aria-label="Página siguiente"
          className={btnCls(hasNext)}
        >
          ›
        </button>
      </nav>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ExportsTable({
  data,
  cancellingIds,
  onDownload,
  onCancel,
  onPageChange,
}: ExportsTableProps) {
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]">
      <table className="w-full border-collapse text-left">
        <thead>
          <TableHead />
        </thead>
        <tbody>
          {data.items.map((item) => (
            <ExportsRow
              key={item.exportId}
              item={item}
              statusLabel={STATUS_LABELS[item.status]}
              onDownload={onDownload}
              onCancel={onCancel}
              isCancelling={cancellingIds.has(item.exportId)}
            />
          ))}
        </tbody>
      </table>
      <TablePagination pagination={data.pagination} onPageChange={onPageChange} />
    </div>
  );
}
